import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWallet } from '@txnlab/use-wallet';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { createListing } from '../utils/marketplace/listing';
import Button from './Button';
import Form from './Form';
import BaseInput from './Input';
import InputLabel from './InputLabel';
import InputWrapper from './InputWrapper';
import Modal from './Modal';
import NftImage from './NftImage';
import party from 'party-js';

const MarketplaceSellForm = ({ kitty }) => {
  const { activeAddress, signer } = useWallet();
  const sender = { signer, addr: activeAddress };
  const [modalOpen, setModalOpen] = useState(false);
  const onClose = () => setModalOpen(false);
  const queryClient = useQueryClient();

  const confettiRef = useRef();

  const form = useForm({
    defaultValues: {
      assetId: kitty.id || kitty.index,
      price: 0,
    },
  });
  const price = form.watch('price');

  // If 1,000 input
  // You receive: 920.00 A
  // Creator receives: 50.00 A
  // Listing side receives: 15.00 A
  // Buying side receives: 15.00 A
  const uiValues = {
    youReceive: price - (price * 0.05 + price * 0.015 + price * 0.015),
    creatorFee: price * 0.05,
    listingFee: price * 0.015,
    buyingFee: price * 0.015,
  };

  const listingMutation = useMutation({
    mutationFn: (values) => createListing(signer, activeAddress, sender, values.assetId, values.price),
    onSuccess: () => {
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      party.confetti(confettiRef.current, {
        count: party.variation.range(20, 60),
      });
      toast.success('Listing created!');
      onClose();
    },
    onError: (error) => {
      if (error?.message?.includes('BigInt')) {
        toast.success('Listing created!');
        onClose();
        return;
      }
      console.error(error);
      toast.error(error.message ?? 'Something went wrong. Please try again later.');
    },
    onSettled: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ['userAssets', activeAddress] });
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    values.price = parseFloat(values.price);

    if (!signer) {
      toast.error('Please connect your wallet');
      return;
    }

    if (!activeAddress) {
      toast.error('Please connect your wallet');
      return;
    }

    if (values.price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    if (typeof values.price !== 'number') {
      toast.error('Price must be a number');
      return;
    }

    // todo - make check if it's actually a kitty and not a block or accessory
    if (values.price < 50 && !window.confirm('Are you sure you want to sell your kitty for less than 50 ALGO?')) {
      return;
    }

    listingMutation.mutate(values);
  };

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, []);

  return (
    <div>
      <div className='mt-2 mb-4 flex justify-center w-full px-4'>
        <Button theme='outline' className='w-full' onClick={() => setModalOpen(true)}>
          List for Sale
        </Button>
      </div>

      <Modal open={modalOpen} onClose={onClose} className='!max-w-2xl'>
        <div className='p-6'>
          <div className='flex md:flex-row flex-col gap-8'>
            <div className='relative aspect-square w-full lg:max-w-[300px]'>
              <NftImage nft={kitty} className='rounded-lg absolute inset-0 object-cover w-full h-full' />
            </div>
            <Form form={form} onSubmit={onSubmit} className='flex flex-col gap-4 flex-1'>
              <div>
                <p className='text-xl font-bold'>{kitty.name}</p>
                <p className='text-sm font-normal text-white/50'>{kitty.collection}</p>

                <ul class='list text-sm text-white/50 mt-3'>
                  <li class='text-base'>You receive: {uiValues.youReceive.toFixed(2)} A</li>
                  <li>Creator receives: {uiValues.creatorFee.toFixed(2)} A</li>
                  <li>Listing side receives: {uiValues.listingFee.toFixed(2)} A</li>
                  <li>Buying side receives: {uiValues.buyingFee.toFixed(2)} A</li>
                </ul>

                <div className='mt-4'>
                  <InputWrapper>
                    <InputLabel>Set Price</InputLabel>
                    <BaseInput name='price' type='number' placeholder='0.00' className='!bg-gray-700 w-full' />
                  </InputWrapper>
                </div>
              </div>
              <div className='flex justify-end gap-2'>
                <Button theme='outline' onClick={onClose}>
                  Cancel
                </Button>
                <Button loading={listingMutation.isLoading} onClick={onSubmit}>
                  List Now
                </Button>
              </div>
              <div ref={confettiRef}></div>
            </Form>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MarketplaceSellForm;
